/* eslint-disable @typescript-eslint/naming-convention */
import { RecommendedAction } from './types/remediation';

export enum EuqTimeRangeEnum {
  Last24h = '24h',
  Last1w = '1w',
  Last1M = '1M',
  Last3M = '3M',
}

export enum FeedbackFilterStatus {
  All = 'all',
  Created = 'created',
  Approved = 'approved',
  Rejected = 'rejected',
  Bypassed = 'bypassed',
}

export enum FeedbackFilterCategory {
  All = 'all',
  Spam = 'spam',
  NotSpam = 'notspam',
  Phishing = 'phishing',
}

export enum FeedbackResultCategory {
  All = 'all',
  Spam = 'spam',
  Phishing = 'threat',
  Flagged = 'flagged',
  Normal = 'normal',
  InProgress = 'in_progress',
  Error = 'error',
}

export enum ServiceTypeFilter {
  All = 'all',
  Outlook = 'outlook',
  Gmail = 'gmail',
}

export enum FeedbackStatisticView {
  PieChart = 'pie',
  LineChart = 'line',
}

export interface EuqFeedbackRecord {
  id: number;
  company_guid: string;
  group_company_guid: string;
  service_type: number;
  category: FeedbackFilterCategory;
  mailbox: string;
  requested_by: string;
  retry_count: number;
  status: FeedbackRecordStatus;
  status_message: string | null;
  unique_message_id: string;
  internet_message_id: string | null;
  message_sender: string;
  message_recipients: string;
  message_subject: string;
  message_arrive_time: string;
  trace_id: string;
  deliver_requested_by: string | null;
  delivered_category: FeedbackFilterCategory;
  gtoc_case_id: string | null;
  gtoc_sr_number: string | null;
  gtoc_result_id: string | null;
  gtoc_result_category: string | null;
  mitigation_status: FeedbackMitigationStatus;
  updated_at: string;
  created_at: string;
  request_source?: 'Plugin' | 'Banner' | 'AutoCollect' | 'CentralOperation';
}

export interface EuqFeedbackContentReqDto {
  status: FeedbackFilterStatus;
  category: FeedbackFilterCategory;
  result_category?: FeedbackResultCategory;
  request_source?: 'Plugin' | 'Banner' | 'AutoCollect' | 'CentralOperation';
  predefined: EuqTimeRangeEnum;
  mitigation_status?: number | null;
  service_type?: ServiceTypeFilter;
  // keyword: string;
  message_recipients: string;
  requested_by: string;
  message_subject: string;
  message_sender: string;
  offset: number;
  limit: number;
  sort: string;
  order: 'desc' | 'asc';
}
export interface EuqFeedbackDownloadItem {
  id: string;
}

export type EuqFeedbackDownloadReqDto = EuqFeedbackDownloadItem[];

export interface FilterKeyword {
  recipient: string;
  sender: string;
  reporter: string;
  subject: string;
}

export type FilterKeywordField = keyof FilterKeyword;

export interface FilterParams {
  status: FeedbackFilterStatus;
  timeRange: EuqTimeRangeEnum;
  category: FeedbackFilterCategory;
  keyword: FilterKeyword;
  pageParams: {
    currentPage: number;
    pageSize: number;
  };
  sortKey: string;
  trigger: Date | null;
}

export type ActionModalState =
  | {
      name: null;
      id: null;
    }
  | {
      name: 'reject' | 'approve';
      id: string;
    };

export enum FeedbackRecordStatus {
  CREATED = 'created',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  BYPASSED = 'bypassed',
  APPROVED_IN_PROGRESS = 'approved:in_progress', // Feedback approved, analysis in progress
  APPROVED_DONE = 'approved:done', // Feedback approved, analysis completed
  APPROVED_FAILED = 'approved:failed', // Feedback approved, analysis failed
}

export enum FeedbackMitigationStatus {
  DONE = 1,
  IN_PROGRESS = 2, // Entry List is generated and being processed
  PARTIAL_INPROGRESS = 3,
  INITIALIZED = 4, // Entry List is generated, but not yet processed
  INITIALIZING = 5, // Entry List is being generated
}

export enum FeedbackMitigationEntryStatus {
  DONE = 1,
  IN_PROGRESS = 2,
  INITIALIZED = 4,
}

export interface FeedbackRelatedEntry {
  id: string;
  company_guid: string;
  mitigation_id: string;
  category: string;
  created_at: string;
  group_company_guid: string;
  message_violators: string;
  message_arrive_time: string;
  message_recipients: string;
  message_sender: string;
  message_subject: string;
  message_id: string;
  status: FeedbackMitigationEntryStatus;
  status_message: string;
  action_batch_id: string;
  updated_at: string;
  recommanded_action: RecommendedAction;
  mitigation_tier?: number;
}

export interface MitigationTierStatistic {
  totalAffectedCount: number;
  totalCount: number;
  doneCount: number;
  pendingCount: number;
}

export interface FeedbackRelatedEntryStatistic {
  totalAffectedCount: number;
  totalCount: number;
  doneCount: number;
  pendingCount: number;
  mitigationTier?: {
    [tier: string]: MitigationTierStatistic;
  };
}

export interface FeedbackRelatedEntryResponse {
  code: number;
  contentList: FeedbackRelatedEntry[];
  total: number;
  statistic: FeedbackRelatedEntryStatistic;
}

export interface FeedbackRelatedEntryReqDto {
  offset: number;
  limit: number;
  sort: string;
  order: 'desc' | 'asc';
}

export interface ExecuteFeedbackActionRequest {
  id: string;
}

export type ExecuteFeedbackActionsRequest = ExecuteFeedbackActionRequest[];

export interface FeedbackResultData {
  version: string;
  caseState: string;
  processingState?: string;
  customSubmit: {
    type: string;
  };
  rescanResult: {
    type: string;
    confidenceLevel: string;
    reasoning: {
      summary: string;
      suspiciousItems?: string[];
      confidenceItems?: string[];
    };
  };
  remediation: {
    basic?: {
      msgId: string;
      scanTime: string;
    }[];
    advance?: {
      msgId: string;
      scanTime: string;
    }[];
  };
  customRule: {
    urls?: string[];
    urlDomains?: string[];
    senderAddresses?: string[];
    senderDomains?: string[];
  };
}

export interface MessageHeader {
  key: string;
  line: string;
}

export interface FeedbackMailData {
  metadata: {
    messageSender: string;
    messageRecipients: string;
    messageSubject: string;
    messageArriveTime: string;
    messageId: string;
    requestedBy: string;
    messageAttachments: {
      filename?: string;
      type: string;
      contentType: string;
      size: number;
    }[];
    messageHeaders: MessageHeader[];
  };
  text: string;
  html: string;
}
